import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";

import type { BoardRow } from "../board.ts";
import {
  CORPORATE_READ_SCOPE,
  costCentreRow,
  employeeRow,
  INVOICES_NOTE,
  invoiceRow,
  OVERVIEW_NOTE_DRAWN,
  PEOPLE_NOTE,
  POLICIES_NOTE,
  policyRow,
  RIDES_NOTE,
  rideRow,
  thisMonth,
} from "../corporate.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * The corporate portal's five boards, on COR-01.
 *
 * Built to `21 WP20 Corporate Portal.dc.html`, which was marked `PROPOSAL — no backing service`
 * until ADR 0095. `../corporate.ts` quotes the design's own row data beside each mapper so a
 * column can be compared against the design without opening the file.
 *
 * ## The account, which every board needs and none of them chooses
 *
 * A travel manager may manage more than one company. `GET /v1/corporate-accounts` answers the
 * ones this person manages, and every other read takes one of those identifiers — so each board
 * resolves the account first and draws nothing until it has. That is one extra request on first
 * paint and it is the right shape: the alternative is an account identifier in configuration,
 * which would be a different portal per company.
 *
 * **COR-01 refuses an account this subject does not manage with a 404 identical to one that does
 * not exist**, so a manipulated identifier reaches the same empty state as a wrong one. These
 * components do not need to know that; it is stated here because a reader looking for the
 * authorization will not find it in this file, and should not.
 */

const corporate = services.customer__corporate_accounts;

export interface CorporateBoardProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Acme Logistics › Company › Employees`, the design's trail. */
  readonly crumbs?: readonly string[];
  /** Injected in tests so "this month" is not a moving target. */
  readonly now?: Date;
}

interface Loaded {
  readonly rows: readonly BoardRow[];
}

/**
 * Resolve the account, then load the board.
 *
 * The two failures are kept apart on purpose. No account at all is not an error — it is a person
 * who manages no company, which the empty state says in its own words — while a failed board
 * read is. Collapsing them would tell somebody their session was broken when their account had
 * simply not been set up.
 */
function useAccountBoard(
  transport: Transport,
  session: Session,
  load: (accountId: string) => Promise<Loaded>,
): {
  readonly loaded: Loaded | undefined;
  readonly failure: ViewState | undefined;
  readonly accountName: string | undefined;
  readonly noAccount: boolean;
  readonly reload: () => void;
} {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [accountName, setAccountName] = useState<string | undefined>(undefined);
  const [noAccount, setNoAccount] = useState(false);

  const run = useCallback(async () => {
    setLoaded(undefined);
    setFailure(undefined);
    setNoAccount(false);

    if (!session.scopes.has(CORPORATE_READ_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [CORPORATE_READ_SCOPE] });
      return;
    }

    try {
      const client = corporate.createCorporateAccountsClient(transport);
      const { accounts } = await client.listMyCorporateAccounts();
      const account = accounts[0];
      if (account === undefined) {
        setNoAccount(true);
        setLoaded({ rows: [] });
        return;
      }
      setAccountName(account.displayName);
      setLoaded(await load(account.accountId));
    } catch (error) {
      setFailure(stateForError(error, [CORPORATE_READ_SCOPE]));
    }
  }, [transport, session.scopes, load]);

  useEffect(() => {
    void run();
  }, [run]);

  return {
    loaded,
    failure,
    accountName,
    noAccount,
    reload: (): void => {
      void run();
    },
  };
}

/** What the empty state says when the person manages no company at all. */
/**
 * The clock a board reads, held still between renders.
 *
 * Three boards took `now = new Date()` as a defaulted parameter and listed `now` in the
 * dependency array of the callback that fetches. A default parameter is evaluated on every
 * render, so `now` was a new object every render, so the callback was new, so the effect that
 * calls it ran again, so it set state, so the component rendered — and set `loaded` back to
 * `undefined` each time round. The board never left `Loading` and the service was asked for
 * the same rows about thirteen times a second.
 *
 * The comment that stood over one of those dependency arrays said the hazard out loud —
 * _“`now` is the clock; including it would re-fetch on every render in a test that passes a
 * fresh Date”_ — and the array included it anyway. It was invisible in production for a
 * different reason: COR-01 refused every request, so the loop was a loop of failures and the
 * board drew an error rather than a spinner. Fixing the 403 is what made this one visible.
 *
 * A caller that passes a fixed `now`, which the tests do, gets exactly that value and a stable
 * identity for it. A caller that passes nothing gets one Date for the lifetime of the board,
 * which is what a board wants: the period it reports is the period it was opened in, and it
 * must not silently change under the reader.
 */
function useStableClock(given: Date | undefined): Date {
  return useMemo(() => given ?? new Date(), [given]);
}

const NO_ACCOUNT = "You do not manage a company account yet. Ask your operator to add you to one.";

export function Overview({
  transport,
  session,
  crumbs,
  now: givenNow,
}: CorporateBoardProps): ReactNode {
  const now = useStableClock(givenNow);
  const load = useCallback(
    async (accountId: string): Promise<Loaded> => {
      const period = thisMonth(now);
      const { costCentres } = await corporate
        .createCorporateAccountsClient(transport)
        .listCostCentres(accountId, period.from, period.to);
      return { rows: costCentres.map(costCentreRow) };
    },
    // `now` is the clock; including it would re-fetch on every render in a test that passes a
    // fresh Date. The period is computed when the fetch runs, which is the moment meant.
    [transport, now],
  );
  const { loaded, failure, noAccount, reload } = useAccountBoard(transport, session, load);

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Overview"
      source="COR-01"
      description="Company travel and deliveries this month, against budget."
      columns={["Cost centre", "Owner", "Spent"]}
      rows={loaded?.rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={reload}
      purpose={noAccount ? NO_ACCOUNT : "No cost centre has been added to this account yet."}
      primary={{
        label: "Add cost centre",
        // Published by ADR 0095 and withheld as a permission rather than as a missing route: a
        // board that can open a budget line is a board that can raise a company's spend.
        refusal:
          "Adding a cost centre needs corporate:write, which this session does not hold. The operation exists.",
      }}
      filter={{ unfiltered: "All cost centres", options: [], value: "", onChange: () => undefined }}
      note={{ kind: "info", glyph: "info", text: OVERVIEW_NOTE_DRAWN }}
      exportName="corporate-cost-centres"
      detailSubtitle="Overview · COR-01"
      detailNote={OVERVIEW_NOTE_DRAWN}
    />
  );
}

export function Employees({
  transport,
  session,
  crumbs,
  now: givenNow,
}: CorporateBoardProps): ReactNode {
  const now = useStableClock(givenNow);
  const load = useCallback(
    async (accountId: string): Promise<Loaded> => {
      const period = thisMonth(now);
      const { employees } = await corporate
        .createCorporateAccountsClient(transport)
        .listCorporateEmployees(accountId, period.from, period.to);
      return { rows: employees.map(employeeRow) };
    },
    [transport, now],
  );
  const { loaded, failure, noAccount, reload } = useAccountBoard(transport, session, load);

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Employees"
      source="COR-01"
      description="Who can ride on the company account, and under which cost centre."
      columns={["Employee", "Cost centre", "Rides this month"]}
      rows={loaded?.rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={reload}
      purpose={noAccount ? NO_ACCOUNT : "Nobody has been invited to this account yet."}
      primary={{
        label: "Invite employees",
        refusal:
          "Inviting employees needs corporate:write, which this session does not hold. The operation exists.",
      }}
      filter={{ unfiltered: "All employees", options: [], value: "", onChange: () => undefined }}
      note={{ kind: "info", glyph: "info", text: PEOPLE_NOTE }}
      exportName="corporate-employees"
      detailSubtitle="Employees · COR-01"
      detailNote={PEOPLE_NOTE}
    />
  );
}

export function TravelPolicies({ transport, session, crumbs }: CorporateBoardProps): ReactNode {
  const load = useCallback(
    async (accountId: string): Promise<Loaded> => {
      const { policies } = await corporate
        .createCorporateAccountsClient(transport)
        .listTravelPolicies(accountId);
      return { rows: policies.map(policyRow) };
    },
    [transport],
  );
  const { loaded, failure, noAccount, reload } = useAccountBoard(transport, session, load);

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Travel policies"
      source="COR-01"
      description="Rules each ride is checked against before it is booked."
      columns={["Policy", "Applies to", "Rule"]}
      rows={loaded?.rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={reload}
      purpose={noAccount ? NO_ACCOUNT : "No travel policy has been written for this account yet."}
      primary={{
        label: "New policy",
        refusal:
          "Writing a policy needs corporate:write, which this session does not hold. The operation exists.",
      }}
      filter={{ unfiltered: "All policies", options: [], value: "", onChange: () => undefined }}
      // `lock`, which is the design's own kind for this page's callout.
      note={{ kind: "lock", glyph: "lock", text: POLICIES_NOTE }}
      exportName="corporate-travel-policies"
      detailSubtitle="Travel policies · COR-01"
      detailNote={POLICIES_NOTE}
    />
  );
}

export function Rides({
  transport,
  session,
  crumbs,
  now: givenNow,
}: CorporateBoardProps): ReactNode {
  const now = useStableClock(givenNow);
  const load = useCallback(
    async (accountId: string): Promise<Loaded> => {
      const period = thisMonth(now);
      const { rides } = await corporate
        .createCorporateAccountsClient(transport)
        .listCorporateRides(accountId, period.from, period.to);
      return { rows: rides.map(rideRow) };
    },
    [transport, now],
  );
  const { loaded, failure, noAccount, reload } = useAccountBoard(transport, session, load);

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Rides"
      source="COR-01"
      description="Every business ride, with its cost centre and policy check."
      columns={["Ride", "Employee", "Fare"]}
      rows={loaded?.rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={reload}
      purpose={
        noAccount
          ? NO_ACCOUNT
          : "No business ride has been posted to this account this month. A ride appears here once the trip domain records it."
      }
      primary={{
        label: "Book a ride",
        /*
         * The one refusal on this portal that is a missing route rather than a permission.
         *
         * COR-01 records that a ride happened; it does not book one, and a corporate booking
         * additionally needs the policy evaluated before the ride is offered — which ADR 0095
         * deliberately leaves outside this service. So no service this page may call publishes
         * the command.
         */
        refusal:
          "No service publishes a corporate booking yet. COR-01 records a ride that happened; booking one is MOB-01's, and the policy check before it has no owner.",
      }}
      filter={{ unfiltered: "All rides", options: [], value: "", onChange: () => undefined }}
      note={{ kind: "info", glyph: "info", text: RIDES_NOTE }}
      exportName="corporate-rides"
      detailSubtitle="Rides · COR-01"
      detailNote={RIDES_NOTE}
    />
  );
}

export function Invoices({ transport, session, crumbs }: CorporateBoardProps): ReactNode {
  const load = useCallback(
    async (accountId: string): Promise<Loaded> => {
      const { invoices } = await corporate
        .createCorporateAccountsClient(transport)
        .listCorporateInvoices(accountId);
      return { rows: invoices.map(invoiceRow) };
    },
    [transport],
  );
  const { loaded, failure, noAccount, reload } = useAccountBoard(transport, session, load);

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Invoices"
      source="COR-01"
      description="Monthly invoices for the company, generated from posted rides."
      columns={["Invoice", "Period", "Amount"]}
      rows={loaded?.rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={reload}
      purpose={noAccount ? NO_ACCOUNT : "No invoice has been issued to this account yet."}
      primary={{
        label: "Update billing",
        refusal:
          "Updating billing details needs corporate:write, which this session does not hold. The operation exists.",
      }}
      filter={{ unfiltered: "All invoices", options: [], value: "", onChange: () => undefined }}
      note={{ kind: "info", glyph: "info", text: INVOICES_NOTE }}
      exportName="corporate-invoices"
      detailSubtitle="Invoices · COR-01"
      detailNote={INVOICES_NOTE}
    />
  );
}
